// useSettings - Theme, playback, and UI settings
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type AccentColor = 'purple' | 'pink' | 'amber' | 'cyan' | 'green';

export interface AppSettings {
  themeMode: ThemeMode;
  accentColor: AccentColor;
  showAdultContent: boolean;
  blurAdultCovers: boolean;
  autoPlay: boolean;
  autoNext: boolean;
  playbackSpeed: number;
  preferredLanguage: 'sub' | 'dub' | 'both';
  performanceMode: 'auto' | 'high' | 'low';
  reducedMotion: boolean;
  defaultSubtitle: 'show' | 'hide';
}

// Accent color map
const ACCENT_COLORS: Record<AccentColor, { hex: string; name: string }> = {
  purple: { hex: '#8B5CF6', name: 'Violet' },
  pink: { hex: '#EC4899', name: 'Pink' },
  amber: { hex: '#F59E0B', name: 'Amber' },
  cyan: { hex: '#06B6D4', name: 'Cyan' },
  green: { hex: '#22C55E', name: 'Green' },
};

interface SettingsContextType {
  settings: AppSettings;
  setSettings: (s: Partial<AppSettings>) => void;
  themeMode: ThemeMode;
  themePreference: 'light' | 'dark';
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  setAccentColor: (color: AccentColor) => void;
  currentAccent: { hex: string; name: string };
  isLowPerformance: boolean;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    return {
      settings: defaultSettings,
      setSettings: () => {},
      themeMode: 'dark' as ThemeMode,
      themePreference: 'dark' as 'light' | 'dark',
      toggleTheme: () => {},
      setThemeMode: () => {},
      setAccentColor: () => {},
      currentAccent: ACCENT_COLORS.purple,
      isLowPerformance: false,
    } as SettingsContextType;
  }
  return ctx;
};

const defaultSettings: AppSettings = {
  themeMode: 'dark',
  accentColor: 'purple',
  showAdultContent: false,
  blurAdultCovers: true,
  autoPlay: true,
  autoNext: true,
  playbackSpeed: 1.0,
  preferredLanguage: 'sub',
  performanceMode: 'auto',
  reducedMotion: false,
  defaultSubtitle: 'show',
};

const SETTINGS_KEY = 'anime_settings';

function loadSettings(): AppSettings {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    if (stored) {
      return { ...defaultSettings, ...JSON.parse(stored) };
    }
  } catch {}
  return defaultSettings;
}

function saveSettings(s: AppSettings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {}
}

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettingsState] = useState<AppSettings>(loadSettings);

  const setSettings = (partial: Partial<AppSettings>) => {
    setSettingsState(prev => {
      const next = { ...prev, ...partial };
      saveSettings(next);
      return next;
    });
  };

  const toggleTheme = () => {
    const next: ThemeMode = settings.themeMode === 'dark' ? 'light'
      : settings.themeMode === 'light' ? 'system'
      : 'dark';
    setSettings({ themeMode: next });
  };

  const setThemeMode = (mode: ThemeMode) => setSettings({ themeMode: mode });
  const setAccentColor = (color: AccentColor) => setSettings({ accentColor: color });

  // Resolve actual theme (light/dark) from themeMode + system preference
  const themePreference: 'light' | 'dark' =
    settings.themeMode === 'system'
      ? (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : settings.themeMode;

  // Apply accent color CSS variable
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--anime-token-brand-primary-main',
      ACCENT_COLORS[settings.accentColor].hex
    );
    document.documentElement.style.setProperty(
      '--anime-gradient-primary',
      `linear-gradient(135deg, ${ACCENT_COLORS[settings.accentColor].hex}, ${ACCENT_COLORS[settings.accentColor].hex}99)`
    );
  }, [settings.accentColor]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    const apply = (mode: 'light' | 'dark') => {
      root.setAttribute('data-color-scheme', mode);
      root.classList.remove('light', 'dark');
      root.classList.add(mode);
    };
    apply(themePreference);
  }, [themePreference]);

  const isLowPerformance =
    settings.performanceMode === 'low' ||
    (settings.performanceMode === 'auto' && navigator.hardwareConcurrency <= 4);

  return (
    <SettingsContext.Provider value={{
      settings,
      setSettings,
      themeMode: settings.themeMode,
      themePreference,
      toggleTheme,
      setThemeMode,
      setAccentColor,
      currentAccent: ACCENT_COLORS[settings.accentColor],
      isLowPerformance,
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

// Export for direct import
export { ACCENT_COLORS };